/**
 * Bonsai Pen — Multi-Practice-Area Contract Drafting Platform
 * Landing page with document type picker + Clause Library
 * SHA 16-step wizard as first live template
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API } from "../config/api";
import axios from "axios";
import { errorMessage } from "../utils/errorMessage";
import { toast } from "sonner";
import { parseBlobError } from "../utils/parseBlobError";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { ScrollArea } from "../components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import {
  ChevronLeft, ChevronRight, FileText, Building2, Users, BarChart3,
  Shield, ArrowRightLeft, AlertTriangle, UserMinus, CheckCircle2,
  Scale, Lock, Handshake, Heart, XCircle, Download,
  Loader2, Sparkles, Search, PenLine, Plus, Trash2, Info,
  BookOpen, Clock, ArrowLeft, ArrowRight, Edit3, Copy, Tag, Filter, X, Library, Paperclip, Calculator, Mail
} from "lucide-react";
import { LOGO_URL } from "../config/constants";
import { STEP_GUIDANCE, FIELD_HINTS, APPROVAL_LEVELS } from "./bonsaiPenShaGuidance";

// Brief 86 — the variant-selector test harness renders <BonsaiPen /> in
// isolation, without a surrounding <Router>. useNavigate() throws outside a
// router context, so fall back to a no-op navigator when none is present.
function useNavigateSafe() {
  try {
    return useNavigate();
  } catch {
    return () => {};
  }
}

const STEPS = [
  { id: 1, label: "Agreement Type", icon: FileText },
  { id: 2, label: "Company", icon: Building2 },
  { id: 3, label: "Shareholders", icon: Users },
  { id: 4, label: "Share Structure", icon: BarChart3 },
  { id: 5, label: "Governance", icon: Shield },
  { id: 6, label: "Reserved Matters", icon: Lock },
  { id: 7, label: "Share Transfer", icon: ArrowRightLeft },
  { id: 8, label: "Events of Default", icon: AlertTriangle },
  { id: 9, label: "Leaver Provisions", icon: UserMinus },
  { id: 10, label: "Completion", icon: CheckCircle2 },
  { id: 11, label: "Drag & Tag", icon: Scale },
  { id: 12, label: "Deadlock", icon: Handshake },
  { id: 13, label: "Restrictions", icon: Lock },
  { id: 14, label: "Cross-Option", icon: Heart },
  { id: 15, label: "Termination", icon: XCircle },
  { id: 16, label: "Review & Generate", icon: Download },
];

// BM-H4222: labels follow the clause 9.2 text; rm20 to rm26 added. Each matter
// carries an approval level (trap 3); a matter left blank is Unanimous Consent.
const RESERVED_MATTERS = [
  { key: "rm1", label: "Change the nature of the business, or cease to be a private company" },
  { key: "rm2", label: "Amend the articles of association" },
  { key: "rm3", label: "Change the company name or registered office" },
  { key: "rm4", label: "Sell the whole or a substantial part of the business or assets" },
  { key: "rm5", label: "Issue, buy back or redeem shares, or alter share capital" },
  { key: "rm6", label: "Vary share rights, or grant options or warrants" },
  { key: "rm7", label: "Transactions outside the ordinary course or not at arm's length" },
  { key: "rm8", label: "Winding up, administration or other insolvency step" },
  { key: "rm9", label: "Mergers, acquisitions, subsidiaries, joint ventures" },
  { key: "rm10", label: "Acquire or dispose of an asset above the contract threshold" },
  { key: "rm11", label: "Enter a contract above a threshold", amountField: "contract_threshold" },
  { key: "rm12", label: "Borrow above a threshold", amountField: "borrowing_threshold" },
  { key: "rm13", label: "Give guarantees, indemnities or security outside the ordinary course" },
  { key: "rm14", label: "Lend money or grant credit outside the ordinary course" },
  { key: "rm15", label: "Appoint or remove a director, or change director pay (other than under clause 5)" },
  { key: "rm16", label: "Pass a resolution at a meeting not every shareholder attends" },
  { key: "rm17", label: "Register a transfer or new member outside the agreement and articles" },
  { key: "rm18", label: "Become tax resident outside the UK" },
  { key: "rm19", label: "Bonus, profit share or share incentive schemes" },
  { key: "rm20", label: "Declare or pay a dividend" },
  { key: "rm21", label: "Adopt or depart from the annual budget, or approve the accounts" },
  { key: "rm22", label: "Change auditors, accounting reference date or accounting policies" },
  { key: "rm23", label: "Start, settle or abandon litigation above a threshold", amountField: "litigation_threshold" },
  { key: "rm24", label: "Transactions with a shareholder or connected person (their votes not counted)" },
  { key: "rm25", label: "License, sell or deal with the company's intellectual property" },
  { key: "rm26", label: "List or trade the company's shares on any market" },
];

const EMPTY_SHAREHOLDER = { name: "", address: "", email: "", num_shares: 0, share_class: "Ordinary", is_director: true };

const defaultForm = () => ({
  template_type: "",
  has_corporate_shareholders: false,
  has_more_than_4: false,
  has_multiple_share_classes: false,
  has_institutional_investors: false,
  company_name: "", company_reg_number: "", company_address: "", accounting_ref_date: "",
  business_description: "", existing_share_capital: false, existing_shares_count: 0,
  existing_sha: false, nominal_value: "1.00", agreement_date: "",
  shareholders: [{ ...EMPTY_SHAREHOLDER }, { ...EMPTY_SHAREHOLDER }],
  different_share_classes: false, share_class_differences: "", consent_type: "unanimous", qualified_majority_pct: 75,
  min_pct_appoint_director: 10, max_directors: 4, min_board_meetings_year: 4,
  board_notice_days: 14, board_quorum: 2, mgmt_accounts_frequency: "quarterly",
  mgmt_accounts_days: 21, annual_accounts_months: 3,
  reserved_matters: Object.fromEntries(RESERVED_MATTERS.map(rm => [rm.key, { enabled: false, threshold: "", level: "unanimous" }])),
  contract_threshold: "", borrowing_threshold: "", litigation_threshold: "",
  all_in_all_out: true, min_director_pct: 0, min_director_resign: false,
  time_commitment_clause: true, time_commitment: "", time_notice_days: 30,
  right_first_refusal: true, transfer_acceptance_days: 14, company_purchase_option: false,
  company_purchase_days: 14, third_party_sale_days: 30, retain_voting_during_transfer: true,
  preemption_new_issues: true, new_issue_acceptance_days: 14, company_attorney: true,
  default_death: true, default_bankruptcy: true, default_cease_director: true,
  default_breach_days: 30, default_breach: true, default_gross_misconduct: true,
  default_criminal: false, default_confidentiality_breach: true,
  bad_leaver_discount: true, bad_leaver_floor_pct: 50,
  good_leaver_option: "B", good_leaver_election_pct: 75, good_leaver_election_months: 3,
  completion_days: 14, late_interest_rate: 4.0, fair_valuation: true, valuer_agreement_days: 14,
  drag_along: true, drag_threshold_pct: 75, drag_notice_days: 14,
  tag_along: true, tag_trigger_pct: 50, tag_notice_days: 14, tag_acceptance_days: 14,
  deadlock_clause: true, deadlock_third_party: false, deadlock_third_party_name: "",
  deadlock_resolve_days: 30, deadlock_mechanism: "mediation",
  // Brief 86 — canonical variant selectors
  director_model: "A", covenant_tier: "standard", deadlock_option: "mediation",
  restrictions_during: true, restrictions_during_detail: "", restrictions_post: true,
  non_compete_months: 12, non_solicitation_months: 12, non_poach_months: 12,
  geographic_scope: "England and Wales", passive_investment_pct: 3.0, confidentiality_years: 3,
  life_assurance: false, life_earmarked: false, valuation_method: "",
  call_option_months: 6, put_option_months: 3, shareholder_values: {},
  policy_deadline_days: 30, policy_review_months: 12, completion_after_policy_days: 14,
  dividends_during_option: "estate", call_option_exercise: "jointly",
  terminate_winding_up: true, terminate_receiver: true, terminate_other: "",
  file_reference: "", additional_instructions: "", matter_ref: null,
});

// ── Field helpers ──
// BM-H4222: `hint` shows a one-line explanation under the field and as a tooltip.
const Hint = ({ text }) => (text ? <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{text}</p> : null);

const YesNo = ({ value, onChange, label, testId, hint }) => (
  <div className="py-2" data-testid={testId} title={hint || undefined}>
    <div className="flex items-center justify-between">
      <Label className="text-sm text-slate-300 flex-1">{label}</Label>
      <Switch checked={value} onCheckedChange={onChange} />
    </div>
    <Hint text={hint} />
  </div>
);

const NumberField = ({ value, onChange, label, suffix, min = 0, hint, testId }) => (
  <div title={hint || undefined} data-testid={testId}>
    <Label className="text-xs text-slate-400 mb-1 block">{label}</Label>
    <div className="flex items-center gap-2">
      <Input type="number" value={value} onChange={e => onChange(Number(e.target.value))} min={min}
        className="bg-navy border-navy-border text-white text-sm w-24" />
      {suffix && <span className="text-xs text-slate-500">{suffix}</span>}
    </div>
    <Hint text={hint} />
  </div>
);

const TextField = ({ value, onChange, label, placeholder, multiline, testId, hint }) => (
  <div data-testid={testId} title={hint || undefined}>
    <Label className="text-xs text-slate-400 mb-1.5 block">{label}</Label>
    {multiline ? (
      <Textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="bg-navy border-navy-border text-white text-sm resize-none" rows={3} />
    ) : (
      <Input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="bg-navy border-navy-border text-white text-sm" />
    )}
    <Hint text={hint} />
  </div>
);

const StepGuidance = ({ step }) => {
  const g = STEP_GUIDANCE[step];
  if (!g) return null;
  return (
    <div className="mb-5 rounded-lg border border-status-amber/40 bg-navy p-4" data-testid={`sha-guidance-step-${step}`}>
      <p className="text-xs font-semibold text-status-amber uppercase tracking-wider mb-2 flex items-center gap-1.5">
        <Info className="w-3.5 h-3.5" /> {g.title}
      </p>
      <ul className="list-disc pl-5 space-y-1">
        {g.points.map((pt, i) => <li key={i} className="text-xs text-slate-300 leading-relaxed">{pt}</li>)}
      </ul>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════
//  Clause Library Panel
// ═══════════════════════════════════════════════════════════════

function ClauseLibraryPanel({ open, onClose, onInsert }) {
  const [clauses, setClauses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [editingClause, setEditingClause] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newClause, setNewClause] = useState({ title: "", category: "Other", body: "", tags: "" });
  const [saving, setSaving] = useState(false);

  const fetchClauses = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCat) params.category = filterCat;
      if (searchQ) params.q = searchQ;
      const resp = await axios.get(`${API}/bonsai-pen/clauses`, { params, withCredentials: true });
      setClauses(resp.data || []);
    } catch { setClauses([]); }
    setLoading(false);
  }, [filterCat, searchQ]);

  const fetchCategories = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/bonsai-pen/clause-categories`, { withCredentials: true });
      setCategories(resp.data || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { if (open) { fetchClauses(); fetchCategories(); } }, [open, fetchClauses, fetchCategories]);

  const handleCreate = async () => {
    if (!newClause.title.trim() || !newClause.body.trim()) { toast.error("Title and body required"); return; }
    setSaving(true);
    try {
      const resp = await axios.post(`${API}/bonsai-pen/clauses`, {
        title: newClause.title, category: newClause.category, body: newClause.body,
        tags: newClause.tags.split(",").map(t => t.trim()).filter(Boolean),
      }, { withCredentials: true });
      toast.success("Clause saved");
      setShowCreate(false);
      setNewClause({ title: "", category: "Other", body: "", tags: "" });
      fetchClauses();
    } catch (e) { toast.error(errorMessage(e, "Save failed")); }
    setSaving(false);
  };

  const handleUpdate = async () => {
    if (!editingClause) return;
    setSaving(true);
    try {
      await axios.put(`${API}/bonsai-pen/clauses/${editingClause.id}`, {
        title: editingClause.title, category: editingClause.category, body: editingClause.body,
        tags: typeof editingClause.tags === "string" ? editingClause.tags.split(",").map(t => t.trim()).filter(Boolean) : editingClause.tags,
      }, { withCredentials: true });
      toast.success("Clause updated");
      setEditingClause(null);
      fetchClauses();
    } catch (e) { toast.error(errorMessage(e, "Update failed")); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/bonsai-pen/clauses/${id}`, { withCredentials: true });
      toast.success("Clause deleted");
      fetchClauses();
    } catch (e) { toast.error("Delete failed"); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-[480px] z-50 bg-navy border-l border-navy-border shadow-2xl flex flex-col" data-testid="clause-library-panel">
      <div className="p-4 border-b border-navy-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Library className="w-4 h-4 text-teal" />
          <h3 className="text-sm font-semibold text-white">Clause Library</h3>
          <Badge className="bg-teal-dim/40 text-teal text-[10px]">{clauses.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => { setShowCreate(true); setEditingClause(null); }} className="h-7 text-xs bg-teal hover:bg-teal" data-testid="create-clause-btn">
            <Plus className="w-3 h-3 mr-1" /> New
          </Button>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1"><X className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Search + Filter */}
      <div className="p-3 border-b border-navy-border space-y-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <Input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search clauses..."
            className="pl-8 bg-navy-card border-navy-border text-white text-sm h-8" data-testid="clause-search" />
        </div>
        <Select value={filterCat} onValueChange={v => setFilterCat(v === "all" ? "" : v)}>
          <SelectTrigger className="bg-navy-card border-navy-border text-white text-xs h-8">
            <Filter className="w-3 h-3 mr-1.5 text-slate-500" />
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Create / Edit form */}
      {(showCreate || editingClause) && (
        <div className="p-4 border-b border-navy-border bg-navy-card space-y-3">
          <h4 className="text-xs font-medium text-teal uppercase tracking-wider">{editingClause ? "Edit Clause" : "New Clause"}</h4>
          <Input value={editingClause ? editingClause.title : newClause.title}
            onChange={e => editingClause ? setEditingClause(p => ({ ...p, title: e.target.value })) : setNewClause(p => ({ ...p, title: e.target.value }))}
            placeholder="Clause title" className="bg-navy border-navy-border text-white text-sm" data-testid="clause-title-input" />
          <Select value={editingClause ? editingClause.category : newClause.category}
            onValueChange={v => editingClause ? setEditingClause(p => ({ ...p, category: v })) : setNewClause(p => ({ ...p, category: v }))}>
            <SelectTrigger className="bg-navy border-navy-border text-white text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Textarea value={editingClause ? editingClause.body : newClause.body}
            onChange={e => editingClause ? setEditingClause(p => ({ ...p, body: e.target.value })) : setNewClause(p => ({ ...p, body: e.target.value }))}
            placeholder="Clause text..." rows={5} className="bg-navy border-navy-border text-white text-sm resize-none" data-testid="clause-body-input" />
          <Input value={editingClause ? (Array.isArray(editingClause.tags) ? editingClause.tags.join(", ") : editingClause.tags || "") : newClause.tags}
            onChange={e => editingClause ? setEditingClause(p => ({ ...p, tags: e.target.value })) : setNewClause(p => ({ ...p, tags: e.target.value }))}
            placeholder="Tags (comma-separated)" className="bg-navy border-navy-border text-white text-sm" />
          <div className="flex gap-2">
            <Button size="sm" onClick={editingClause ? handleUpdate : handleCreate} disabled={saving} className="flex-1 h-7 text-xs bg-teal hover:bg-teal" data-testid="save-clause-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} {editingClause ? "Update" : "Save"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setShowCreate(false); setEditingClause(null); }} className="h-7 text-xs border-navy-border text-slate-400">Cancel</Button>
          </div>
        </div>
      )}

      {/* Clause list */}
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-2">
          {loading && <div className="text-center py-8 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" />Loading...</div>}
          {!loading && clauses.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Library className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No clauses yet</p>
              <p className="text-xs mt-1">Create your first reusable clause</p>
            </div>
          )}
          {clauses.map(clause => (
            <Card key={clause.id} className="bg-navy-card border-navy-border hover:border-teal/50 transition-colors" data-testid={`clause-${clause.id}`}>
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-white truncate">{clause.title}</p>
                    <Badge className="bg-navy-hover text-slate-400 text-[9px] mt-0.5">{clause.category}</Badge>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {onInsert && (
                      <button onClick={() => onInsert(clause)} className="text-teal hover:text-teal p-1" title="Insert into instructions" data-testid={`insert-clause-${clause.id}`}>
                        <Copy className="w-3 h-3" />
                      </button>
                    )}
                    <button onClick={() => setEditingClause({ ...clause, tags: Array.isArray(clause.tags) ? clause.tags.join(", ") : "" })} className="text-blue-400 hover:text-blue-300 p-1"><Edit3 className="w-3 h-3" /></button>
                    <button onClick={() => handleDelete(clause.id)} className="text-status-red hover:text-status-red p-1"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
                <p className="text-xs text-slate-400 line-clamp-3">{clause.body}</p>
                {clause.tags?.length > 0 && (
                  <div className="flex gap-1 mt-1.5 flex-wrap">
                    {clause.tags.map((t, i) => <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-navy-hover text-slate-500">{t}</span>)}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
//  Practice Area Icons
// ═══════════════════════════════════════════════════════════════
const PA_ICONS = { sha: Users, spa: ArrowRightLeft, apa: Building2, loan: Scale, nda: Lock, partnership: Handshake };
const PA_COLORS = { sha: "emerald", spa: "blue", apa: "amber", loan: "purple", nda: "cyan", partnership: "orange" };

// ═══════════════════════════════════════════════════════════════
//  SHA Wizard (original 16-step flow)
// ═══════════════════════════════════════════════════════════════

function SHAWizard({ onBack, clauseLibraryOpen, onToggleClauseLibrary, onInsertClause, initialStep, clientLinkToken }) {
  const navigate = useNavigateSafe();
  const [step, setStep] = useState(initialStep ?? 1);
  const [form, setForm] = useState(defaultForm);
  const [generating, setGenerating] = useState(false);
  // Brief 10 (28 May 2026): multimodal reference attachments (PDF, DOCX,
  // images, .eml, .txt, .md). Passed alongside the wizard JSON via
  // multipart/form-data to /api/bonsai-pen/generate.
  const [attachments, setAttachments] = useState([]);
  const [attachWarn, setAttachWarn] = useState("");
  const fileInputRef = useRef(null);
  const [matterSearch, setMatterSearch] = useState("");
  const [matterResults, setMatterResults] = useState([]);
  const [matterSearching, setMatterSearching] = useState(false);
  const matterTimerRef = useRef(null);
  const [clientLinkLoadState, setClientLinkLoadState] = useState(clientLinkToken ? "loading" : "idle");

  // Client answers already use BonsaiPenRequest field names/types.  The
  // handoff is therefore a simple shallow overlay on the normal blank form,
  // with no client-side translation layer and no alternate generate path.
  useEffect(() => {
    if (!clientLinkToken) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(
          `${API}/bonsai-pen/client-link/${encodeURIComponent(clientLinkToken)}/for-staff-wizard`,
          { withCredentials: true },
        );
        if (cancelled) return;
        setForm(prev => ({ ...prev, ...(response.data || {}) }));
        setClientLinkLoadState("loaded");
        toast.success("Client questionnaire loaded for review");
      } catch (e) {
        if (cancelled) return;
        setClientLinkLoadState("error");
        toast.error(errorMessage(e, "Could not load the client questionnaire"));
      }
    })();
    return () => { cancelled = true; };
  }, [clientLinkToken]);

  const set = useCallback((key, val) => setForm(prev => ({ ...prev, [key]: val })), []);

  // Handle clause insertion from library
  useEffect(() => {
    if (onInsertClause) {
      onInsertClause.current = (clause) => {
        setForm(prev => {
          const separator = prev.additional_instructions.trim() ? "\n\n---\n\n" : "";
          return {
            ...prev,
            additional_instructions: prev.additional_instructions + separator + `[${clause.title}]\n${clause.body}`,
          };
        });
        setStep(16); // Jump to review step to see inserted clause
        toast.success(`"${clause.title}" inserted into additional instructions`);
      };
    }
  }, [onInsertClause]);
  const setSh = useCallback((idx, key, val) => {
    setForm(prev => {
      const next = [...prev.shareholders];
      next[idx] = { ...next[idx], [key]: val };
      return { ...prev, shareholders: next };
    });
  }, []);
  const setRM = useCallback((key, field, val) => {
    setForm(prev => ({
      ...prev,
      reserved_matters: { ...prev.reserved_matters, [key]: { ...prev.reserved_matters[key], [field]: val } },
    }));
  }, []);

  const addShareholder = () => {
    if (form.shareholders.length >= 4) return;
    setForm(prev => ({ ...prev, shareholders: [...prev.shareholders, { ...EMPTY_SHAREHOLDER }] }));
  };
  const removeShareholder = (idx) => {
    if (form.shareholders.length <= 2) return;
    setForm(prev => ({ ...prev, shareholders: prev.shareholders.filter((_, i) => i !== idx) }));
  };

  // Matter search
  const searchMatters = useCallback(async (q) => {
    if (q.length < 2) { setMatterResults([]); return; }
    setMatterSearching(true);
    try {
      const userId = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "";
      const resp = await axios.get(`${API}/bonsai/filing/matters`, {
        params: { q, limit: 6 },
        headers: userId ? { "X-Bonsai-User-Id": userId } : {},
        withCredentials: true,
      });
      setMatterResults(resp.data || []);
    } catch { setMatterResults([]); }
    setMatterSearching(false);
  }, []);

  const handleMatterSearch = (e) => {
    const q = e.target.value;
    setMatterSearch(q);
    if (matterTimerRef.current) clearTimeout(matterTimerRef.current);
    matterTimerRef.current = setTimeout(() => searchMatters(q), 300);
  };

  // Navigation
  const canProceed = () => {
    if (step === 1 && !form.template_type) return false;
    if (step === 2 && !form.company_name.trim()) return false;
    if (step === 3 && form.shareholders.filter(s => s.name.trim()).length < 2) return false;
    return true;
  };

  const goNext = () => { if (step < 16 && canProceed()) setStep(s => s + 1); };
  const goPrev = () => { if (step > 1) setStep(s => s - 1); };
  const goTo = (s) => setStep(s);

  // Skip cross-option step if not applicable
  const effectiveStep = step === 14 && form.template_type === "sha_only" ? 15 : step;

  // Generate
  const handleGenerate = async () => {
    setGenerating(true);
    setAttachWarn("");
    try {
      // Brief 10: multipart/form-data — `payload` is the JSON-stringified
      // wizard request, `attachments` is the optional reference-doc list.
      const fd = new FormData();
      fd.append("payload", JSON.stringify(form));
      attachments.forEach(f => fd.append("attachments", f, f.name));
      const resp = await axios.post(`${API}/bonsai-pen/generate`, fd, {
        responseType: "blob",
        timeout: 240000,  // 4 min — multimodal calls take longer with large precedents
        withCredentials: true,
        // NB: do NOT set Content-Type; axios sets multipart boundary
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement("a");
      link.href = url;
      const cd = resp.headers["content-disposition"];
      const match = cd?.match(/filename="(.+?)"/);
      link.download = match ? match[1] : `${form.company_name || "SHA"} - Contract.docx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(
        attachments.length
          ? `Contract generated (with ${attachments.length} reference document${attachments.length === 1 ? "" : "s"})`
          : "Contract generated and downloaded"
      );
    } catch (e) {
      const errText = await parseBlobError(e);
      toast.error("Generation failed: " + errText);
    }
    setGenerating(false);
  };

  const totalShares = form.shareholders.reduce((s, sh) => s + (sh.num_shares || 0), 0);

  // ═══════════════════════════════════════
  // Step renderers
  // ═══════════════════════════════════════

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-1">What type of document do you need?</h3>
        <p className="text-sm text-slate-400 mb-4">Select the template variant</p>
        <div className="grid gap-3">
          {[
            { value: "sha_only", title: "SHA Only", desc: "Shareholders' Agreement without cross-option provisions" },
            { value: "sha_cross_embedded", title: "SHA with Embedded Cross-Option", desc: "SHA with the deed of cross-option as Schedule 4 (one document)" },
            { value: "sha_cross_standalone", title: "SHA + Standalone Cross-Option", desc: "SHA plus a separate deed of cross-option, after a page break in the same Word file" },
          ].map(opt => (
            <Card key={opt.value}
              className={`cursor-pointer transition-all border-2 ${form.template_type === opt.value
                ? "border-teal bg-teal-dim/20" : "border-navy-border bg-navy hover:border-teal/50"}`}
              onClick={() => set("template_type", opt.value)}
              data-testid={`template-${opt.value}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${form.template_type === opt.value ? "border-teal" : "border-slate-500"}`}>
                    {form.template_type === opt.value && <div className="w-2 h-2 rounded-full bg-teal" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{opt.title}</p>
                    <p className="text-xs text-slate-400">{opt.desc}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <TextField value={form.company_name} onChange={v => set("company_name", v)} label="Company full name *" placeholder="Example Ltd" testId="company-name" />
      <TextField value={form.company_reg_number} onChange={v => set("company_reg_number", v)} label="Company Registration Number" placeholder="12345678" />
      <TextField value={form.company_address} onChange={v => set("company_address", v)} label="Registered Address" placeholder="123 Main Street, London" multiline />
      <div className="grid grid-cols-2 gap-4">
        <TextField value={form.accounting_ref_date} onChange={v => set("accounting_ref_date", v)} label="Accounting Reference Date" placeholder="31 March" />
        <TextField value={form.nominal_value} onChange={v => set("nominal_value", v)} label="Nominal value per share (£)" placeholder="1.00" />
      </div>
      <TextField value={form.business_description} onChange={v => set("business_description", v)} label="Nature of the business (5-10 words)" placeholder="Software development consultancy" />
      <YesNo value={form.existing_share_capital} onChange={v => set("existing_share_capital", v)} label="Does the company already have share capital?" />
      {form.existing_share_capital && (
        <NumberField value={form.existing_shares_count} onChange={v => set("existing_shares_count", v)} label="How many shares issued?" suffix="shares" />
      )}
      <YesNo value={form.existing_sha} onChange={v => set("existing_sha", v)} label="Is there a current shareholders' agreement?" hint={FIELD_HINTS.existing_sha} />
      <TextField value={form.agreement_date} onChange={v => set("agreement_date", v)} label="Intended execution date" placeholder="e.g. 15 April 2026" />
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-white">Shareholders ({form.shareholders.length})</h3>
        {form.shareholders.length < 4 && (
          <Button size="sm" variant="outline" onClick={addShareholder} className="text-xs h-7 border-teal text-teal" data-testid="add-shareholder-btn">
            <Plus className="w-3 h-3 mr-1" /> Add shareholder
          </Button>
        )}
      </div>
      {form.shareholders.map((sh, i) => (
        <Card key={i} className="bg-navy border-navy-border">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-teal uppercase tracking-wider">Shareholder {String.fromCharCode(65 + i)}</span>
              {form.shareholders.length > 2 && (
                <button onClick={() => removeShareholder(i)} className="text-status-red hover:text-status-red p-1"><Trash2 className="w-3.5 h-3.5" /></button>
              )}
            </div>
            <Input value={sh.name} onChange={e => setSh(i, "name", e.target.value)} placeholder="Full legal name *" className="bg-navy-card border-navy-border text-white text-sm" data-testid={`sh-name-${i}`} />
            <Textarea value={sh.address} onChange={e => setSh(i, "address", e.target.value)} placeholder="Address" rows={2} className="bg-navy-card border-navy-border text-white text-sm resize-none" />
            <Input value={sh.email} onChange={e => setSh(i, "email", e.target.value)} placeholder="Email" className="bg-navy-card border-navy-border text-white text-sm" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] text-slate-500 mb-1 block">Number of Shares</Label>
                <Input type="number" value={sh.num_shares || ""} onChange={e => setSh(i, "num_shares", parseInt(e.target.value) || 0)}
                  className="bg-navy-card border-navy-border text-white text-sm" min={0} />
              </div>
              <div>
                <Label className="text-[10px] text-slate-500 mb-1 block">Share Class</Label>
                <Input value={sh.share_class} onChange={e => setSh(i, "share_class", e.target.value)}
                  className="bg-navy-card border-navy-border text-white text-sm" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs text-slate-400">Also a director?</Label>
              <Switch checked={sh.is_director} onCheckedChange={v => setSh(i, "is_director", v)} />
            </div>
            {totalShares > 0 && sh.num_shares > 0 && (
              <p className="text-[10px] text-teal">{((sh.num_shares / totalShares) * 100).toFixed(1)}% of total shares</p>
            )}
          </CardContent>
        </Card>
      ))}
      {totalShares > 0 && (
        <div className="text-xs text-slate-400 text-right">Total: {totalShares.toLocaleString()} shares</div>
      )}
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-4">
      <YesNo value={form.different_share_classes} onChange={v => set("different_share_classes", v)} label="Are there different share classes with different voting or dividend rights?" />
      {form.different_share_classes && (
        <TextField value={form.share_class_differences} onChange={v => set("share_class_differences", v)} label="Describe the differences" multiline placeholder="e.g. Class A shares carry 2 votes each, Class B carry 1 vote..." />
      )}
      {/* BM-H4222: approval levels are chosen per reserved matter on Step 6. */}
      <NumberField value={form.qualified_majority_pct} onChange={v => set("qualified_majority_pct", v)} label="Qualified majority percentage" suffix="% of the voting shares" hint={FIELD_HINTS.qualified_majority_pct} testId="sha-qualified-majority-pct" />
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-4">
      {/* Brief 86 — director model selector (G-C1) */}
      <div>
        <Label className="text-xs text-slate-400 mb-1.5 block">Director model</Label>
        <select
          data-testid="bonsai-director-model-select"
          value={form.director_model ?? "A"}
          onChange={(e) => set("director_model", e.target.value)}
          className="w-full rounded border border-navy-border bg-navy text-white px-3 py-2 text-sm"
        >
          <option value="A">A — Appointment right by shareholding</option>
          <option value="B">B — Founder lock-in</option>
          <option value="C">C — Investor director (founder protection)</option>
        </select>
        <p className="mt-1 text-xs text-slate-500">
          Selects the clause 5.1 body. Defaults to A. Clauses 5.2 to 5.6 (bad
          leaver removal, resignation on exit, s 168, casting vote, time
          commitment) apply whichever model is selected.
        </p>
        <span data-testid="bonsai-director-model-current" className="hidden">
          {form.director_model ?? "A"}
        </span>
      </div>
      {/* BM-H4367 trap 18: the agreed time commitment and removal for not giving it. */}
      <div className="border-t border-navy-border pt-4 space-y-3" data-testid="sha-time-commitment-section">
        <YesNo value={form.time_commitment_clause} onChange={v => set("time_commitment_clause", v)} label="Include a time commitment for founder-directors?" hint={FIELD_HINTS.time_commitment_clause} testId="sha-time-commitment-clause" />
        {form.time_commitment_clause && (
          <div className="grid grid-cols-2 gap-4">
            <TextField value={form.time_commitment} onChange={v => set("time_commitment", v)} label="Agreed time commitment" placeholder="e.g. at least four days a week" hint={FIELD_HINTS.time_commitment} testId="sha-time-commitment" />
            <NumberField value={form.time_notice_days} onChange={v => set("time_notice_days", v)} label="Notice before removal" suffix="days" hint={FIELD_HINTS.time_notice_days} testId="sha-time-notice-days" />
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <NumberField value={form.min_pct_appoint_director} onChange={v => set("min_pct_appoint_director", v)} label="Min % to appoint director" suffix="%" hint={FIELD_HINTS.min_pct_appoint_director} />
        <NumberField value={form.max_directors} onChange={v => set("max_directors", v)} label="Maximum directors" />
        <NumberField value={form.min_board_meetings_year} onChange={v => set("min_board_meetings_year", v)} label="Min Board meetings/year" />
        <NumberField value={form.board_notice_days} onChange={v => set("board_notice_days", v)} label="Board notice period" suffix="days" />
        <NumberField value={form.board_quorum} onChange={v => set("board_quorum", v)} label="Board quorum" suffix="directors" />
      </div>
      <div className="border-t border-navy-border pt-4">
        <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-3">Accounts & Reporting</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-slate-400 mb-1.5 block">Management accounts frequency</Label>
            <Select value={form.mgmt_accounts_frequency} onValueChange={v => set("mgmt_accounts_frequency", v)}>
              <SelectTrigger className="bg-navy border-navy-border text-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="six-monthly">Six-monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <NumberField value={form.mgmt_accounts_days} onChange={v => set("mgmt_accounts_days", v)} label="Delivery deadline" suffix="days after period" />
          <NumberField value={form.annual_accounts_months} onChange={v => set("annual_accounts_months", v)} label="Annual accounts deadline" suffix="months after year end" />
        </div>
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-3">
      <p className="text-xs text-slate-400 mb-2">Choose an approval level for each matter. Unanimous Consent is the default; &quot;Not reserved&quot; leaves the matter out.</p>
      {RESERVED_MATTERS.map(rm => {
        const level = form.reserved_matters[rm.key]?.level || "unanimous";
        return (
          <div key={rm.key} className={`p-3 rounded-lg border transition-colors ${level !== "not_reserved" ? "border-teal/50 bg-teal-dim/10" : "border-navy-border bg-navy"}`}>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-300 flex-1">{rm.label}</span>
              <select
                data-testid={`sha-rm-level-${rm.key}`}
                value={level}
                onChange={e => setRM(rm.key, "level", e.target.value)}
                className="rounded border border-navy-border bg-navy text-white px-2 py-1 text-xs"
              >
                {APPROVAL_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>
            {level !== "not_reserved" && rm.amountField && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs text-slate-500">£</span>
                <Input value={form[rm.amountField] || ""}
                  onChange={e => set(rm.amountField, e.target.value)}
                  placeholder="e.g. 25,000" className="bg-navy-card border-navy-border text-white text-sm w-40"
                  data-testid={`sha-rm-amount-${rm.key}`} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderStep7 = () => (
    <div className="space-y-4">
      <YesNo value={form.all_in_all_out} onChange={v => set("all_in_all_out", v)} label="Must a shareholder who sells sell all their shares?" hint={FIELD_HINTS.all_in_all_out} />
      {!form.all_in_all_out && (
        <>
          <YesNo value={form.min_director_resign} onChange={v => set("min_director_resign", v)} label="If a director-shareholder falls below a minimum %, must they resign?" />
          {form.min_director_resign && (
            <NumberField value={form.min_director_pct} onChange={v => set("min_director_pct", v)} label="Minimum director shareholding" suffix="%" />
          )}
        </>
      )}
      {/* BM-H4222: pre-emption on transfer is always included (traps 7 to 9). */}
      <NumberField value={form.transfer_acceptance_days} onChange={v => set("transfer_acceptance_days", v)} label="Pre-emption acceptance period" suffix="Business Days" hint={FIELD_HINTS.transfer_acceptance_days} />
      <YesNo value={form.company_purchase_option} onChange={v => set("company_purchase_option", v)} label="Can the company purchase surplus shares?" hint={FIELD_HINTS.company_purchase_option} />
      {form.company_purchase_option && (
        <NumberField value={form.company_purchase_days} onChange={v => set("company_purchase_days", v)} label="Company purchase period" suffix="Business Days" />
      )}
      <NumberField value={form.third_party_sale_days} onChange={v => set("third_party_sale_days", v)} label="Days to complete third-party sale" suffix="days" />
      <YesNo value={form.retain_voting_during_transfer} onChange={v => set("retain_voting_during_transfer", v)} label="Keep votes and dividends while a deemed transfer is under way?" hint={FIELD_HINTS.retain_voting_during_transfer} />
      <YesNo value={form.preemption_new_issues} onChange={v => set("preemption_new_issues", v)} label="Pre-emption rights on newly issued shares?" hint={FIELD_HINTS.preemption_new_issues} />
      {form.preemption_new_issues && (
        <NumberField value={form.new_issue_acceptance_days} onChange={v => set("new_issue_acceptance_days", v)} label="New issue acceptance period" suffix="Business Days" />
      )}
      <p className="text-[11px] text-slate-500">The power of attorney to the Company for transfers is always included (clause 27.11); it needs the agreement signed as a deed.</p>
    </div>
  );

  const renderStep8 = () => (
    <div className="space-y-4">
      <p className="text-xs text-slate-400 mb-2">Which events trigger compulsory share transfer?</p>
      <YesNo value={form.default_death} onChange={v => set("default_death", v)} label="Death" hint={FIELD_HINTS.default_death} />
      <YesNo value={form.default_bankruptcy} onChange={v => set("default_bankruptcy", v)} label="Bankruptcy / insolvency" />
      <YesNo value={form.default_cease_director} onChange={v => set("default_cease_director", v)} label="Ceasing to be a director (makes them a Leaver)" hint={FIELD_HINTS.default_cease_director} />
      <YesNo value={form.default_breach} onChange={v => set("default_breach", v)} label="Failure to remedy material breach" />
      {form.default_breach && (
        <NumberField value={form.default_breach_days} onChange={v => set("default_breach_days", v)} label="Cure period" suffix="Business Days" />
      )}
      <YesNo value={form.default_gross_misconduct} onChange={v => set("default_gross_misconduct", v)} label="Gross misconduct / fraud / dishonesty" />
      <YesNo value={form.default_criminal} onChange={v => set("default_criminal", v)} label="Criminal conviction (non-road traffic)" />
      <YesNo value={form.default_confidentiality_breach} onChange={v => set("default_confidentiality_breach", v)} label="Serious breach of confidentiality / non-compete" />
    </div>
  );

  const renderStep9 = () => (
    <div className="space-y-4">
      <YesNo value={form.bad_leaver_discount} onChange={v => set("bad_leaver_discount", v)} label="Bad leaver gets the lower of cost and Fair Value?" hint={FIELD_HINTS.bad_leaver_discount} />
      {form.bad_leaver_discount && (
        <NumberField value={form.bad_leaver_floor_pct} onChange={v => set("bad_leaver_floor_pct", v)} label="Price floor for breach-type bad leavers" suffix="% of Fair Value" hint={FIELD_HINTS.bad_leaver_floor_pct} testId="sha-bad-leaver-floor" />
      )}
      <div className="border-t border-navy-border pt-4">
        <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-3">Good Leaver</p>
        <div title={FIELD_HINTS.good_leaver_option}>
          <Label className="text-xs text-slate-400 mb-1.5 block">Good Leaver option</Label>
          <Select value={form.good_leaver_option} onValueChange={v => set("good_leaver_option", v)}>
            <SelectTrigger className="bg-navy border-navy-border text-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="B">Option B: must sell at Fair Value (all in, all out, recommended)</SelectItem>
              <SelectItem value="A">Option A: keeps shares unless the others elect to buy</SelectItem>
            </SelectContent>
          </Select>
          <Hint text={FIELD_HINTS.good_leaver_option} />
        </div>
        {form.good_leaver_option === "A" && (
          <div className="grid grid-cols-2 gap-4 mt-3">
            <NumberField value={form.good_leaver_election_pct} onChange={v => set("good_leaver_election_pct", v)} label="% of remaining shareholders to require sale" suffix="%" />
            <NumberField value={form.good_leaver_election_months} onChange={v => set("good_leaver_election_months", v)} label="Election period" suffix="months" />
          </div>
        )}
      </div>
    </div>
  );

  const renderStep10 = () => (
    <div className="space-y-4">
      <div>
        <Label className="text-xs text-slate-400 mb-1.5 block">Days to complete share purchase</Label>
        <Hint text={FIELD_HINTS.completion_days} />
        <Select value={String(form.completion_days)} onValueChange={v => set("completion_days", parseInt(v))}>
          <SelectTrigger className="bg-navy border-navy-border text-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 Business Days</SelectItem>
            <SelectItem value="14">14 Business Days</SelectItem>
            <SelectItem value="28">28 Business Days</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <NumberField value={form.late_interest_rate} onChange={v => set("late_interest_rate", v)} label="Late payment interest rate" suffix="% above BoE base" hint={FIELD_HINTS.late_interest_rate} />
      <YesNo value={form.fair_valuation} onChange={v => set("fair_valuation", v)} label="Fair valuation by company's accountants?" />
      <NumberField value={form.valuer_agreement_days} onChange={v => set("valuer_agreement_days", v)} label="Days to agree independent Valuer" suffix="Business Days" />
    </div>
  );

  const renderStep11 = () => (
    <div className="space-y-4">
      <div className="p-4 rounded-lg border border-navy-border bg-navy">
        <YesNo value={form.drag_along} onChange={v => set("drag_along", v)} label="Include drag-along rights?" />
        {form.drag_along && (
          <div className="mt-3 space-y-3 ml-2 pl-4 border-l-2 border-teal">
            <NumberField value={form.drag_threshold_pct} onChange={v => set("drag_threshold_pct", v)} label="Majority % to trigger drag" suffix="%" hint={FIELD_HINTS.drag_threshold_pct} />
            <NumberField value={form.drag_notice_days} onChange={v => set("drag_notice_days", v)} label="Notice to called shareholders" suffix="Business Days" />
          </div>
        )}
      </div>
      <div className="p-4 rounded-lg border border-navy-border bg-navy">
        <YesNo value={form.tag_along} onChange={v => set("tag_along", v)} label="Include tag-along rights?" />
        {form.tag_along && (
          <div className="mt-3 space-y-3 ml-2 pl-4 border-l-2 border-blue-800">
            <NumberField value={form.tag_trigger_pct} onChange={v => set("tag_trigger_pct", v)} label="Controlling interest trigger" suffix="%" hint={FIELD_HINTS.tag_trigger_pct} />
            <NumberField value={form.tag_notice_days} onChange={v => set("tag_notice_days", v)} label="Tag offer notice" suffix="Business Days" />
            <NumberField value={form.tag_acceptance_days} onChange={v => set("tag_acceptance_days", v)} label="Tag acceptance period" suffix="Business Days" />
          </div>
        )}
      </div>
    </div>
  );

  const renderStep12 = () => (
    <div className="space-y-4">
      {/* Brief 86 — deadlock option selector (G-C2) */}
      <div>
        <Label className="text-xs text-slate-400 mb-1.5 block">Deadlock resolution route</Label>
        <select
          data-testid="bonsai-deadlock-option-select"
          value={form.deadlock_option ?? "mediation"}
          onChange={(e) => {
            set("deadlock_option", e.target.value);
            // Brief 86 Addendum A P1 #11 — dual-write legacy field for one release.
            const legacyMap = {
              special_resolution: "arbitration",
              mediation: "mediation",
              russian_roulette: "buyout",
            };
            set("deadlock_mechanism", legacyMap[e.target.value] || "mediation");
          }}
          className="w-full rounded border border-navy-border bg-navy text-white px-3 py-2 text-sm"
        >
          <option value="special_resolution">Special resolution (not for 50/50 companies)</option>
          <option value="mediation">Mediation then arbitration</option>
          <option value="russian_roulette">Russian roulette buy/sell</option>
        </select>
        <p className="mt-1 text-xs text-slate-500">
          Selects the clause 11.1 body. Defaults to mediation. Clause 23.1.5
          (liquidation fallback) is emitted only when Russian roulette is chosen.
        </p>
        <span data-testid="bonsai-deadlock-option-current" className="hidden">
          {form.deadlock_option ?? "mediation"}
        </span>
      </div>
      <YesNo value={form.deadlock_clause} onChange={v => set("deadlock_clause", v)} label="Include a Deadlock clause?" />
      {form.deadlock_clause && (
        <>
          <NumberField value={form.deadlock_resolve_days} onChange={v => set("deadlock_resolve_days", v)} label="Days for founders to resolve between themselves" suffix="days" />
          <YesNo value={form.deadlock_third_party} onChange={v => set("deadlock_third_party", v)} label="Do you have an agreed third party / mediator?" hint={FIELD_HINTS.deadlock_third_party} />
          {form.deadlock_third_party && (
            <TextField value={form.deadlock_third_party_name} onChange={v => set("deadlock_third_party_name", v)} label="Third party name" placeholder="e.g. John Smith, Mediator" />
          )}
        </>
      )}
    </div>
  );

  const renderStep13 = () => (
    <div className="space-y-4">
      {/* Brief 86 — covenant tier selector (G-C3) */}
      <div>
        <Label className="text-xs text-slate-400 mb-1.5 block">Restrictive covenant tier</Label>
        <select
          data-testid="bonsai-covenant-tier-select"
          value={form.covenant_tier ?? "standard"}
          onChange={(e) => set("covenant_tier", e.target.value)}
          className="w-full rounded border border-navy-border bg-navy text-white px-3 py-2 text-sm"
        >
          <option value="light">Light (no restrictions)</option>
          <option value="standard">Standard (Board may release)</option>
          <option value="investor">Investor-grade (only Shareholder Consent may release)</option>
        </select>
        <p className="mt-1 text-xs text-slate-500">
          Selects the clause 20.1 body. Defaults to Standard. The month and
          geography fields below populate the leaf values inside the chosen tier.
        </p>
        <span data-testid="bonsai-covenant-tier-current" className="hidden">
          {form.covenant_tier ?? "standard"}
        </span>
      </div>
      <YesNo value={form.restrictions_during} onChange={v => set("restrictions_during", v)} label="Restrictions during term (working elsewhere, competition)?" />
      {form.restrictions_during && (
        <TextField value={form.restrictions_during_detail} onChange={v => set("restrictions_during_detail", v)} label="Details of restrictions during term" multiline placeholder="e.g. Shareholders shall not engage in any competing business..." hint={FIELD_HINTS.restrictions_during_detail} />
      )}
      <YesNo value={form.restrictions_post} onChange={v => set("restrictions_post", v)} label="Post-shareholding restrictions (after selling all shares)?" />
      {form.restrictions_post && (
        <div className="space-y-3 ml-2 pl-4 border-l-2 border-teal">
          <div className="grid grid-cols-3 gap-3">
            <NumberField value={form.non_compete_months} onChange={v => set("non_compete_months", v)} label="Non-compete" suffix="months" hint={FIELD_HINTS.non_compete_months} />
            <NumberField value={form.non_solicitation_months} onChange={v => set("non_solicitation_months", v)} label="Non-solicitation" suffix="months" />
            <NumberField value={form.non_poach_months} onChange={v => set("non_poach_months", v)} label="Non-poaching" suffix="months" />
          </div>
          <TextField value={form.geographic_scope} onChange={v => set("geographic_scope", v)} label="Geographic scope" placeholder="England and Wales" hint={FIELD_HINTS.geographic_scope} />
          <NumberField value={form.passive_investment_pct} onChange={v => set("passive_investment_pct", v)} label="Passive investment carve-out" suffix="% in listed companies" />
        </div>
      )}
      <NumberField value={form.confidentiality_years} onChange={v => set("confidentiality_years", v)} label="Post-termination confidentiality" suffix="years" />
    </div>
  );

  const renderStep14 = () => {
    if (form.template_type === "sha_only") {
      return (
        <div className="text-center py-12 text-slate-400">
          <Heart className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Cross-option provisions are not included in SHA-only template.</p>
          <p className="text-xs mt-2">If needed, go back to Step 1 and select a cross-option variant.</p>
        </div>
      );
    }
    return (
      <div className="space-y-4">
        <YesNo value={form.life_assurance} onChange={v => set("life_assurance", v)} label="Is there a life assurance policy for each shareholder?" hint={FIELD_HINTS.life_assurance} />
        {form.life_assurance && (
          <YesNo value={form.life_earmarked} onChange={v => set("life_earmarked", v)} label="Has the policy been earmarked for share purchase?" />
        )}
        <TextField value={form.valuation_method} onChange={v => set("valuation_method", v)} label="How is the company to be valued at time of death/transfer?" multiline placeholder="e.g. Fair Value determined by the company's auditors..." />
        <div className="grid grid-cols-2 gap-4">
          <NumberField value={form.call_option_months} onChange={v => set("call_option_months", v)} label="Call Option period" suffix="months from the grant" hint={FIELD_HINTS.call_option_months} />
          <NumberField value={form.put_option_months} onChange={v => set("put_option_months", v)} label="Put Option period" suffix="months after call expires" hint={FIELD_HINTS.put_option_months} />
          <NumberField value={form.policy_deadline_days} onChange={v => set("policy_deadline_days", v)} label="Policy take-out deadline" suffix="Business Days" />
          <NumberField value={form.policy_review_months} onChange={v => set("policy_review_months", v)} label="Policy review frequency" suffix="months" />
          <NumberField value={form.completion_after_policy_days} onChange={v => set("completion_after_policy_days", v)} label="Completion after policy pays" suffix="Business Days" />
        </div>
        <div>
          <Label className="text-xs text-slate-400 mb-1.5 block">Dividends during option period</Label>
          <Select value={form.dividends_during_option} onValueChange={v => set("dividends_during_option", v)}>
            <SelectTrigger className="bg-navy border-navy-border text-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="estate">Paid to deceased's estate</SelectItem>
              <SelectItem value="surviving">Paid to surviving shareholders</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-400 mb-1.5 block">Call option exercise</Label>
          <Select value={form.call_option_exercise} onValueChange={v => set("call_option_exercise", v)}>
            <SelectTrigger className="bg-navy border-navy-border text-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="jointly">All surviving shareholders exercise jointly</SelectItem>
              <SelectItem value="individually">Each may exercise individually</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="border-t border-navy-border pt-4">
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">Cover Amount for each shareholder (Appendix 3)</p>
          <Hint text={FIELD_HINTS.shareholder_values} />
          {form.shareholders.filter(s => s.name).map((sh, i) => (
            <div key={i} className="flex items-center gap-3 mb-2">
              <span className="text-xs text-slate-400 w-32 truncate">{sh.name || `Shareholder ${i + 1}`}</span>
              <Input value={form.shareholder_values[sh.name] || ""} onChange={e => {
                set("shareholder_values", { ...form.shareholder_values, [sh.name]: e.target.value });
              }} placeholder="e.g. 100,000" className="bg-navy border-navy-border text-white text-sm flex-1" />
            </div>
          ))}
          <p className="text-[10px] text-status-amber mt-2 flex items-center gap-1">
            <Info className="w-3 h-3" /> Values should be confirmed by the shareholders' accountant or IFA
          </p>
        </div>
      </div>
    );
  };

  const renderStep15 = () => (
    <div className="space-y-4">
      <p className="text-xs text-slate-400 mb-2">The agreement terminates if:</p>
      <YesNo value={form.terminate_winding_up} onChange={v => set("terminate_winding_up", v)} label="A resolution for winding up is passed" />
      <YesNo value={form.terminate_receiver} onChange={v => set("terminate_receiver", v)} label="A receiver, administrator or administrative receiver is appointed" />
      <TextField value={form.terminate_other} onChange={v => set("terminate_other", v)} label="Any other termination events" multiline placeholder="Additional triggers..." hint={FIELD_HINTS.terminate_other} />
    </div>
  );

  const renderStep16 = () => {
    const templateLabel = { sha_only: "SHA Only", sha_cross_embedded: "SHA + Embedded Cross-Option", sha_cross_standalone: "SHA + Standalone Cross-Option" }[form.template_type] || "Unknown";
    const namedShareholders = form.shareholders.filter(s => s.name.trim());
    return (
      <div className="space-y-6">
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-teal uppercase tracking-wider">Summary</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="text-slate-400">Template:</div><div className="text-white">{templateLabel}</div>
            <div className="text-slate-400">Company:</div><div className="text-white">{form.company_name || "—"}</div>
            <div className="text-slate-400">Reg. Number:</div><div className="text-white">{form.company_reg_number || "—"}</div>
            <div className="text-slate-400">Shareholders:</div><div className="text-white">{namedShareholders.map(s => s.name).join(", ") || "—"}</div>
            <div className="text-slate-400">Total Shares:</div><div className="text-white">{totalShares.toLocaleString()}</div>
            <div className="text-slate-400">Reserved matters:</div><div className="text-white" data-testid="bonsai-review-reserved-matters">{RESERVED_MATTERS.filter(rm => (form.reserved_matters[rm.key]?.level || "unanimous") !== "not_reserved").length} of {RESERVED_MATTERS.length} reserved (qualified majority {form.qualified_majority_pct}%)</div>
            <div className="text-slate-400">Drag-along:</div><div className="text-white">{form.drag_along ? `Yes (${form.drag_threshold_pct}%)` : "No"}</div>
            <div className="text-slate-400">Tag-along:</div><div className="text-white">{form.tag_along ? `Yes (>${form.tag_trigger_pct}%)` : "No"}</div>
            <div className="text-slate-400">Deadlock:</div><div className="text-white">{form.deadlock_clause ? "Yes" : "No"}</div>
            <div className="text-slate-400">Bad Leaver:</div><div className="text-white" data-testid="bonsai-review-bad-leaver">{form.bad_leaver_discount ? `Lower of cost and Fair Value, floor ${form.bad_leaver_floor_pct}%` : "Fair Value"}</div>
            {/* Brief 86 — canonical variant chips (G-C4 / G-C5) */}
            <div className="text-slate-400">Director model:</div><div className="text-white" data-testid="bonsai-review-director-model">{form.director_model ?? "A"}</div>
            <div className="text-slate-400">Deadlock route:</div><div className="text-white" data-testid="bonsai-review-deadlock-option">{form.deadlock_option ?? "mediation"}</div>
            <div className="text-slate-400">Covenant tier:</div><div className="text-white" data-testid="bonsai-review-covenant-tier">{form.covenant_tier ?? "standard"}</div>
          </div>
        </div>

        <div className="border-t border-navy-border pt-4 space-y-4">
          <TextField value={form.file_reference} onChange={v => set("file_reference", v)} label="Bonsai Law file reference" placeholder="e.g. VEC.002.0516" testId="file-reference" />
          <TextField value={form.additional_instructions} onChange={v => set("additional_instructions", v)} label="Additional instructions (optional)" multiline placeholder="Any special requirements or clauses..." />

          <div>
            <Label className="text-xs text-slate-400 mb-1.5 block">Link to existing matter (optional)</Label>
            {form.matter_ref ? (
              <div className="flex items-center gap-2 px-3 py-2 rounded border border-teal/40 bg-teal-dim/10">
                <span className="text-sm text-teal flex-1">{form.matter_ref}</span>
                <button onClick={() => set("matter_ref", null)} className="text-slate-500 hover:text-slate-300"><XCircle className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                <Input value={matterSearch} onChange={handleMatterSearch} placeholder="Search matters..."
                  className="pl-8 bg-navy border-navy-border text-white text-sm" data-testid="matter-search" />
                {matterSearching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-teal animate-spin" />}
                {matterResults.length > 0 && (
                  <div className="absolute z-20 top-full mt-1 left-0 right-0 border border-navy-border rounded bg-navy-card max-h-40 overflow-y-auto shadow-xl">
                    {matterResults.map((m, i) => (
                      <div key={i} onClick={() => { set("matter_ref", m.reference || m.matter_reference); setMatterResults([]); setMatterSearch(""); }}
                        className="px-3 py-2 hover:bg-teal-dim/30 cursor-pointer border-b border-navy-border/50 last:border-0">
                        <div className="text-xs text-white font-medium">{m.matter_title || m.reference || "Unknown"}</div>
                        <div className="text-[10px] text-slate-500">{m.reference}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-navy-border pt-4 space-y-3" data-testid="attachments-panel">
          <Label className="text-xs text-slate-400">Reference documents (optional)</Label>
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Attach term sheets, counsel&apos;s notes, precedent contracts, client emails, or images
            (whiteboards / signed pages). Claude will use them as reference. The wizard answers
            above remain authoritative if anything contradicts.
          </p>
          <div className="space-y-2">
            {attachments.map((f, i) => (
              <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 rounded bg-navy-card border border-navy-border text-xs" data-testid={`attachment-row-${i}`}>
                <Paperclip className="w-3 h-3 text-slate-500 shrink-0" />
                <span className="flex-1 truncate text-slate-200" title={f.name}>{f.name}</span>
                <span className="text-[10px] text-slate-500 shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                <button
                  onClick={() => setAttachments(a => a.filter((_, j) => j !== i))}
                  className="text-slate-500 hover:text-rose-400"
                  data-testid={`attachment-remove-${i}`}
                  aria-label={`Remove ${f.name}`}>
                  <XCircle className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.docx,.png,.jpg,.jpeg,.gif,.webp,.txt,.md,.eml"
            onChange={(e) => {
              const picked = Array.from(e.target.files || []);
              setAttachments(prev => [...prev, ...picked]);
              e.target.value = "";
            }}
            className="hidden"
            data-testid="attachments-input"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            data-testid="attachments-add"
            className="text-xs h-8 bg-navy border-navy-border text-slate-300 hover:bg-navy-card">
            <Paperclip className="w-3.5 h-3.5 mr-1.5" /> Add reference document
          </Button>
          {attachWarn && <p className="text-[11px] text-rose-400" data-testid="attachments-warn">{attachWarn}</p>}
        </div>

        <Button onClick={handleGenerate} disabled={generating || !form.company_name.trim()}
          className="w-full bg-teal hover:bg-teal text-white h-12 text-base font-semibold"
          data-testid="generate-btn">
          {generating ? (
            <><Loader2 className="w-5 h-5 animate-spin mr-2" />
              {attachments.length > 0
                ? `Generating with AI (processing ${attachments.length} attachment${attachments.length === 1 ? "" : "s"}, may take 60-120s)...`
                : "Generating with AI (this may take 30-60s)..."}</>
          ) : (
            <><Sparkles className="w-5 h-5 mr-2" />Generate Contract</>
          )}
        </Button>
      </div>
    );
  };

  const stepRenderers = {
    1: renderStep1, 2: renderStep2, 3: renderStep3, 4: renderStep4,
    5: renderStep5, 6: renderStep6, 7: renderStep7, 8: renderStep8,
    9: renderStep9, 10: renderStep10, 11: renderStep11, 12: renderStep12,
    13: renderStep13, 14: renderStep14, 15: renderStep15, 16: renderStep16,
  };

  const currentStep = STEPS.find(s => s.id === step);
  const CurrentIcon = currentStep?.icon || FileText;

  return (
    <div className="min-h-screen bg-navy-card flex" data-testid="sha-wizard">
      {/* Brief 86 — test-only step jump controls (no visible UI in production). */}
      {process.env.NODE_ENV === "test" && (
        <div className="hidden">
          {STEPS.map(s => (
            <button key={s.id} data-testid={`wizard-jump-step-${s.id}`} onClick={() => goTo(s.id)}>
              {s.id}
            </button>
          ))}
        </div>
      )}
      {/* Sidebar — Step navigation */}
      <div className="w-64 border-r border-navy-border bg-navy flex-shrink-0 flex flex-col">
        <div className="p-4 border-b border-navy-border">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-teal transition-colors mb-3" data-testid="back-to-picker">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Dashboard
          </button>
          <div className="flex items-center gap-2">
            <PenLine className="w-4 h-4 text-teal" />
            <span className="text-sm font-bold text-teal">SHA Builder</span>
          </div>
          <p className="text-[10px] text-slate-500 mt-1">Shareholders' Agreement</p>
        </div>
        <ScrollArea className="flex-1">
          <div className="py-2">
            {STEPS.map(s => {
              const Icon = s.icon;
              const isActive = s.id === step;
              const isDone = s.id < step;
              const isSkipped = s.id === 14 && form.template_type === "sha_only";
              return (
                <button key={s.id} onClick={() => goTo(s.id)}
                  className={`w-full flex items-center gap-2.5 px-4 py-2 text-left transition-colors ${
                    isActive ? "bg-teal-dim/30 text-teal border-r-2 border-teal"
                    : isDone ? "text-teal hover:bg-teal-dim/10"
                    : isSkipped ? "text-slate-600 opacity-50"
                    : "text-slate-400 hover:text-slate-300 hover:bg-navy-card"
                  }`}
                  data-testid={`step-nav-${s.id}`}
                >
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 ${
                    isActive ? "bg-teal text-white" : isDone ? "bg-teal-dim text-teal" : "bg-navy-hover text-slate-500"
                  }`}>
                    {isDone ? <CheckCircle2 className="w-3 h-3" /> : s.id}
                  </div>
                  <span className="text-xs truncate">{s.label}</span>
                </button>
              );
            })}
          </div>
        </ScrollArea>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-8 py-5 border-b border-navy-border bg-navy-card">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-teal-dim/40 flex items-center justify-center">
                <CurrentIcon className="w-4 h-4 text-teal" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Step {step}: {currentStep?.label}</h2>
                <p className="text-xs text-slate-500">Step {step} of 16</p>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={onToggleClauseLibrary}
              className={`gap-1.5 text-xs h-8 ${clauseLibraryOpen ? "border-teal text-teal" : "border-navy-border text-slate-400"}`} data-testid="toggle-clause-library">
              <Library className="w-3.5 h-3.5" /> Clause Library
            </Button>
          </div>
          {clientLinkLoadState !== "idle" && (
            <p
              className={`mt-3 text-xs ${clientLinkLoadState === "error" ? "text-status-red" : "text-slate-400"}`}
              data-testid="client-submission-load-status"
            >
              {clientLinkLoadState === "loading" && "Loading the client questionnaire…"}
              {clientLinkLoadState === "loaded" && "Client questionnaire loaded — review and adjust as needed"}
              {clientLinkLoadState === "error" && "The client questionnaire could not be loaded"}
            </p>
          )}
          {/* Progress bar */}
          <div className="mt-3 h-1 bg-navy-hover rounded-full overflow-hidden">
            <div className="h-full bg-teal transition-all duration-300 rounded-full" style={{ width: `${(step / 16) * 100}%` }} />
          </div>
        </div>

        {/* Step content */}
        <ScrollArea className="flex-1">
          <div className="max-w-2xl mx-auto px-8 py-6">
            <StepGuidance step={step} />
            {stepRenderers[step]?.()}
          </div>
        </ScrollArea>

        {/* Bottom nav */}
        <div className="px-8 py-4 border-t border-navy-border bg-navy flex items-center justify-between">
          <Button variant="outline" onClick={goPrev} disabled={step === 1} className="gap-1.5 border-navy-border text-slate-300" data-testid="prev-btn">
            <ChevronLeft className="w-4 h-4" /> Previous
          </Button>
          <span className="text-xs text-slate-500">{step} / 16</span>
          {step < 16 ? (
            <Button onClick={goNext} disabled={!canProceed()} className="gap-1.5 bg-teal hover:bg-teal text-white" data-testid="next-btn">
              Next <ChevronRight className="w-4 h-4" />
            </Button>
          ) : (
            <div />
          )}
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
//  Landing tiles — same look as the Bonsai Agents cards (#4750):
//  each tool has its own two-stop hue, a colour wash, a gradient icon
//  tile and a "go" arrow. Green stays out of the hues (status colour).
// ═══════════════════════════════════════════════════════════════

const PEN_HUES = {
  sha: ["#6366f1", "#a855f7"],
  docs: ["#3b82f6", "#6366f1"],
  tcs: ["#06b6d4", "#0ea5e9"],
  calculator: ["#f97316", "#f43f5e"],
  lba: ["#ec4899", "#d946ef"],
};

function PenSection({ title, children }) {
  return (
    <div className="mb-8">
      <h2 className="text-xs font-semibold uppercase tracking-wider mb-3 agents-title inline-block">{title}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
    </div>
  );
}

function PenTile({ testid, icon: Icon, label, tag, blurb, meta, cta, hue, onClick }) {
  const [c1, c2] = hue;
  return (
    <div
      className="agent-card group relative rounded-2xl overflow-hidden border border-white/10 bg-[#0f1830] transition-all duration-200 hover:-translate-y-1"
      style={{ "--c1": c1, "--c2": c2 }}
    >
      <div aria-hidden className="absolute inset-0 opacity-60 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: `radial-gradient(120% 90% at 0% 0%, ${c1}38, transparent 60%), radial-gradient(90% 80% at 100% 100%, ${c2}24, transparent 60%)` }} />
      <div aria-hidden className="absolute inset-x-0 top-0 h-1" style={{ background: `linear-gradient(90deg, ${c1}, ${c2})` }} />
      <button
        onClick={onClick}
        data-testid={testid}
        className="relative w-full h-full flex flex-col text-left p-5 bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
      >
        <div className="flex items-start gap-4">
          <div className="relative shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ring-1 ring-white/25 transition-transform duration-200 group-hover:scale-110 group-hover:rotate-3"
            style={{ background: `linear-gradient(135deg, ${c1}, ${c2})`, boxShadow: `0 8px 24px -6px ${c1}aa` }}>
            <Icon size={22} className="text-white" />
            <Sparkles size={12} className="agents-twinkle absolute -top-1 -right-1 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-white leading-tight">{label}</span>
              {tag && (
                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                  style={{ color: "#fff", background: `${c1}55` }}>{tag}</span>
              )}
            </div>
            <p className="text-xs text-slate-300/90 mt-1.5 leading-relaxed">{blurb}</p>
            {meta && <p className="text-[10px] text-slate-400 mt-2">{meta}</p>}
          </div>
        </div>
        <div className="mt-auto pt-4 flex items-center justify-between text-xs font-semibold">
          <span className="agent-cta truncate">{cta}</span>
          <span className="w-7 h-7 rounded-full flex items-center justify-center text-white transition-transform duration-200 group-hover:translate-x-1"
            style={{ background: `linear-gradient(135deg, ${c1}, ${c2})` }}>
            <ArrowRight size={14} />
          </span>
        </div>
      </button>
    </div>
  );
}
// ═══════════════════════════════════════════════════════════════
//  Main BonsaiPen Landing + Router
// ═══════════════════════════════════════════════════════════════

export default function BonsaiPen({ initialStep } = {}) {
  const navigate = useNavigateSafe();
  // Brief 86 — when an initialStep is supplied (test harness), open the SHA
  // wizard directly at that step instead of the landing picker.
  const [view, setView] = useState(initialStep != null ? "sha" : "landing"); // "landing" | "sha"
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [clauseLibraryOpen, setClauseLibraryOpen] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [loadingPAs, setLoadingPAs] = useState(true);
  const insertClauseRef = useRef(null);
  const clientLinkToken = new URLSearchParams(
    typeof window === "undefined" ? "" : window.location.search,
  ).get("client_link") || "";

  useEffect(() => {
    (async () => {
      try {
        const [paResp, draftsResp] = await Promise.all([
          axios.get(`${API}/bonsai-pen/practice-areas`, { withCredentials: true }),
          axios.get(`${API}/bonsai-pen/drafts`, { withCredentials: true }),
        ]);
        setPracticeAreas(paResp.data || []);
        setDrafts(draftsResp.data || []);
      } catch { /* silent */ }
      setLoadingPAs(false);
    })();
  }, []);

  // ?sha=1 opens the SHA wizard directly (the document creator's picker links here).
  const shaDirect = new URLSearchParams(
    typeof window === "undefined" ? "" : window.location.search,
  ).get("sha") === "1";

  useEffect(() => {
    if (clientLinkToken || shaDirect) setView("sha");
  }, [clientLinkToken, shaDirect]);

  const handleInsertClause = (clause) => {
    if (insertClauseRef.current) insertClauseRef.current(clause);
  };

  if (view === "sha") {
    return (
      <>
        <SHAWizard
          onBack={() => navigate("/dashboard")}
          clauseLibraryOpen={clauseLibraryOpen}
          onToggleClauseLibrary={() => setClauseLibraryOpen(p => !p)}
          onInsertClause={insertClauseRef}
          initialStep={initialStep}
          clientLinkToken={clientLinkToken}
        />
        <ClauseLibraryPanel
          open={clauseLibraryOpen}
          onClose={() => setClauseLibraryOpen(false)}
          onInsert={handleInsertClause}
        />
      </>
    );
  }

  // Landing page
  const liveAreas = practiceAreas.filter(p => p.status === "live");
  const comingSoon = practiceAreas.filter(p => p.status === "coming_soon");

  return (
    <div className="min-h-screen bg-navy-card" data-testid="bonsai-pen-page">
      <div className="p-6 max-w-6xl mx-auto">
        {/* Hero — the Bonsai Agents band, so the two pages read as a pair. */}
        <div className="agents-hero relative overflow-hidden rounded-2xl border border-white/10 p-6 mb-6 shadow-xl shadow-indigo-950/40">
          <div aria-hidden className="agents-orb absolute -top-28 -right-20 w-80 h-80 rounded-full"
            style={{ background: "radial-gradient(circle, rgba(236,72,153,0.35), transparent 65%)" }} />
          <div aria-hidden className="agents-orb agents-orb-slow absolute -bottom-32 left-1/3 w-96 h-96 rounded-full"
            style={{ background: "radial-gradient(circle, rgba(14,165,233,0.25), transparent 65%)" }} />
          <div className="relative flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="relative w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg shadow-fuchsia-900/40 ring-1 ring-white/20"
                style={{ background: "linear-gradient(135deg, #6366f1, #a855f7 55%, #ec4899)" }}>
                <PenLine size={28} className="text-white" />
                <Sparkles size={16} className="agents-twinkle absolute -top-1.5 -right-1.5 text-amber-200" />
              </div>
              <div>
                <h1 className="text-2xl font-bold agents-title">Bonsai Pen</h1>
                <p className="text-sm text-indigo-100/80 mt-0.5">
                  Drafting, client documents and debt recovery, all in house style.
                </p>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => setClauseLibraryOpen(true)}
              className="gap-1.5 text-xs border-white/20 bg-white/5 text-indigo-100 hover:text-white hover:border-white/40" data-testid="open-clause-library">
              <Library className="w-3.5 h-3.5" /> Clause Library
            </Button>
          </div>
        </div>

        <PenSection title="Corporate and commercial">
          {liveAreas.map(pa => (
            <PenTile
              key={pa.id}
              testid={`pa-card-${pa.id}`}
              icon={PA_ICONS[pa.id] || FileText}
              label={pa.name}
              tag={pa.short}
              blurb={pa.description}
              meta={`${pa.templates?.length || 0} template${pa.templates?.length !== 1 ? "s" : ""} · ${pa.steps} steps`}
              cta="Start drafting"
              hue={PEN_HUES[pa.id] || PEN_HUES.sha}
              onClick={() => setView(pa.id)}
            />
          ))}
          {/* Document creator (#5057): every playbook first draft on one picker. */}
          <PenTile
            testid="pa-card-docs"
            icon={FileText}
            label="Document creator"
            tag="House style"
            blurb="NDAs, services and consultancy agreements, DPAs, IP assignments, board minutes and written resolutions. Word drafts with a drafter's note."
            cta="Create a first draft"
            hue={PEN_HUES.docs}
            onClick={() => navigate("/bonsai-pen/docs")}
          />
        </PenSection>

        {/* Client-facing documents — Brief 89 T&Cs generator (own route). */}
        <PenSection title="Client-facing documents">
          <PenTile
            testid="pa-card-tcs"
            icon={Scale}
            label="Terms & Conditions Generator"
            tag="T&Cs"
            blurb="Generate client-facing terms and conditions with full firm branding."
            cta="Build terms"
            hue={PEN_HUES.tcs}
            onClick={() => navigate("/bonsai-pen/tcs")}
          />
        </PenSection>

        {/* BM-H4482 (Vanessa, 19 Oct 2026): late payment tools. The calculator stands on its own;
            the letter before claim can be linked to a matter. */}
        <PenSection title="Debt recovery">
          <PenTile
            testid="pa-card-late-payment-calculator"
            icon={Calculator}
            label="Late Payment Interest Calculator"
            tag="1998 Act"
            blurb="Statutory interest and fixed sums, or a contract rate, per invoice to the penny."
            cta="Work out interest"
            hue={PEN_HUES.calculator}
            onClick={() => navigate("/bonsai-pen/late-payment-calculator")}
          />
          <PenTile
            testid="pa-card-late-payment-letter"
            icon={Mail}
            label="Letter Before Claim: Late Payment"
            tag="LBA"
            blurb="Checks, figures and a draft for a solicitor to approve. Link it to a matter for the diary dates."
            cta="Draft the letter"
            hue={PEN_HUES.lba}
            onClick={() => navigate("/bonsai-pen/late-payment")}
          />
        </PenSection>

        {/* Coming soon */}
        {comingSoon.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Coming soon</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {comingSoon.map(pa => {
                const Icon = PA_ICONS[pa.id] || FileText;
                return (
                  <div key={pa.id} className="rounded-2xl border border-white/10 bg-[#0f1830]/60 p-5 opacity-70" data-testid={`pa-card-${pa.id}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-navy-hover flex items-center justify-center ring-1 ring-white/10">
                        <Icon className="w-6 h-6 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-sm font-medium text-slate-400">{pa.name}</h3>
                          <Badge className="bg-navy-hover text-slate-500 text-[9px]">{pa.short}</Badge>
                        </div>
                        <p className="text-xs text-slate-500 mb-2">{pa.description}</p>
                        <span className="inline-flex items-center text-[10px] text-slate-400">
                          <Clock className="w-2.5 h-2.5 mr-1" /> Coming soon
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Recent drafts */}
        {drafts.length > 0 && (
          <div>
            <h2 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-4">Recent Drafts</h2>
            <div className="space-y-2">
              {drafts.slice(0, 5).map(d => (
                <div key={d.id} className="flex items-center gap-4 px-4 py-3 rounded-lg border border-navy-border bg-navy hover:border-teal/50 transition-colors">
                  <FileText className="w-4 h-4 text-slate-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{d.company_name || "Untitled draft"}</p>
                    <p className="text-[10px] text-slate-500">{d.template_type?.replace(/_/g, " ")} — {d.updated_at ? new Date(d.updated_at).toLocaleDateString("en-GB") : ""}</p>
                  </div>
                  <Badge className="bg-navy-hover text-slate-500 text-[9px]">Draft</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {loadingPAs && (
          <div className="text-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-teal mx-auto" />
          </div>
        )}
      </div>

      <ClauseLibraryPanel
        open={clauseLibraryOpen}
        onClose={() => setClauseLibraryOpen(false)}
        onInsert={null}
      />
    </div>
  );
}
